import { useCallback, useEffect, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { getProfile, resendVerification } from "~/api/auth"
import { PageShell } from "~/components/PageShell"

export function VerifyEmailPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const verificationQuery = searchParams.get("email_verification")
  const [email, setEmail] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    setLoading(true)
    getProfile()
      .then((data) => {
        setEmail(data.user.email)
        if (data.user.email_verified) {
          navigate("/dashboard", { replace: true })
        }
      })
      .catch(() => navigate("/login", { replace: true }))
      .finally(() => setLoading(false))
  }, [navigate])

  useEffect(() => {
    load()
  }, [load])

  async function handleResend() {
    setError(null)
    setMessage(null)
    setSending(true)
    try {
      await resendVerification()
      setMessage("Verification email sent. Check your inbox.")
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not send email")
    } finally {
      setSending(false)
    }
  }

  if (loading) {
    return (
      <PageShell>
        <p className="text-[var(--color-text-secondary)]">Loading...</p>
      </PageShell>
    )
  }

  return (
    <PageShell innerClassName="max-w-lg w-full">
      <section className="w-full rounded-lg bg-[var(--color-surface-card)] p-6 shadow-sm">
        <h1 className="text-2xl font-semibold text-[var(--color-text-primary)]">Verify your email</h1>
        {verificationQuery === "invalid" && (
          <p className="mt-4 text-sm text-red-600" role="alert">
            This verification link is invalid or has expired. Request a new one below.
          </p>
        )}
        <p className="mt-4 text-sm text-[var(--color-text-secondary)]">
          We sent a message to <span className="font-medium text-[var(--color-text-primary)]">{email}</span>.
          Open the link in that email to finish setting up your account. The link works for 30 days.
        </p>
        {message && (
          <p className="mt-4 text-sm text-green-700" role="status">
            {message}
          </p>
        )}
        {error && (
          <p className="mt-4 text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
          <button
            type="button"
            disabled={sending}
            onClick={() => void handleResend()}
            className="rounded-lg bg-[var(--color-text-primary)] px-6 py-3 font-medium text-[var(--color-surface-card)] transition hover:opacity-90 disabled:opacity-50"
          >
            {sending ? "Sending…" : "Resend verification email"}
          </button>
          <Link
            to="/dashboard"
            className="text-center text-sm font-medium text-[var(--color-text-secondary)] underline-offset-2 hover:underline sm:text-left"
          >
            Back to dashboard
          </Link>
        </div>
      </section>
    </PageShell>
  )
}
